import { Box, FlexLayout } from '@deity/falcon-ui';
import { Button, Icon, Input } from '@deity-io/portal-ui';
import { SchemaActionTypes, SchemaContext } from 'contexts/SchemaContext';
import type { FC } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { isValidFieldName } from 'utils';

interface FieldNameEditorProps {
  isEditable: boolean;
  fieldName: string;
  fieldPath: string[];
}
const FieldNameEditor: FC<FieldNameEditorProps> = ({ isEditable, fieldName, fieldPath }) => {
  const ctx = useContext(SchemaContext);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setEditing(false);
  }, [fieldPath]);

  const onEditFinish = (newFieldName: string) => {
    ctx?.dispatch({ type: SchemaActionTypes.UPDATE_NAME, payload: { path: fieldPath, fieldName: newFieldName } });
    setEditing(false);
  };

  return (
    <Box mt="xl">
      <FlexLayout css={{ position: 'relative' }} width="full">
        {!editing && (
          <FlexLayout pt="xs" pb="xs" width="full" justifyContent="space-between" alignItems="center">
            <Input
              disabled
              required
              label="Name"
              type="text"
              name="name"
              id="name"
              value={fieldName}
              autoComplete="off"
              fullWidth
            />
            {isEditable && (
              <Box style={{ position: 'absolute', right: 0 }}>
                <Button variant="outlined" startIcon={<Icon icon="edit" />} onClick={() => setEditing(true)} />
              </Box>
            )}
          </FlexLayout>
        )}
        {editing && (
          <InputWithConfirmation
            value={fieldName}
            onCancel={val => setEditing(!val)}
            onConfirm={val => onEditFinish(val)}
          />
        )}
      </FlexLayout>
    </Box>
  );
};

export default FieldNameEditor;

type InputWithConfirmationProps = {
  value: string;
  onConfirm: (value) => void;
  onCancel: (val: boolean) => void;
};
const InputWithConfirmation: FC<InputWithConfirmationProps> = ({ value, onConfirm, onCancel }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  const isValid = isValidFieldName.test(inputValue);

  return (
    <FlexLayout pt="xs" pb="xs" css={{ position: 'relative' }} width="full">
      <Input
        autoFocus
        required
        label="Name"
        type="text"
        name="name"
        id="name"
        value={inputValue}
        placeholder="Please enter field name"
        onChange={e => setInputValue(e.target.value)}
        error={!isValid}
        fullWidth
        helperText={isValid ? '' : 'Invalid field name'}
        autoComplete="off"
        // @ts-ignore
        onKeyPress={e => {
          if (e.key === 'Enter') {
            if (isValidFieldName.test(e.target.value)) {
              onConfirm(e.target.value);
            }
          }
        }}
        // @ts-ignore
        onKeyUp={e => {
          if (e.key === 'Escape') {
            onCancel(true);
          }
        }}
      />

      <FlexLayout width="full" alignItems="center" justifyContent="space-between">
        <FlexLayout ml="auto">
          <Button
            variant="outlined"
            sx={{ ml: 2 }}
            disabled={!isValidFieldName.test(inputValue) || value === inputValue}
            startIcon={<Icon icon="done" />}
            onClick={() => {
              if (isValidFieldName.test(inputValue)) {
                onConfirm(inputValue);
              }
            }}
          />
          <Button variant="outlined" startIcon={<Icon icon="close" />} onClick={() => onCancel(true)} />
        </FlexLayout>
      </FlexLayout>
    </FlexLayout>
  );
};
